import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { salesItemService, SalesItemDTO } from '../../services/salesItemService';
import '../../pages/Products/ProductCategoryDetailPage.css';
import { ArrowLeft, PenLine, Trash2, LayoutGrid } from 'lucide-react';

const SalesItemDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [item, setItem] = useState<SalesItemDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadItem();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadItem = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await salesItemService.getSalesItemById(parseInt(id || '0'));
      if (response?.data?.data) {
        setItem(response.data.data);
      }
      setLoading(false);
    } catch {
      setError(t('salesItem.failedToLoadItem'));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="detail-container">
          <div className="alert alert-danger">{error || t('salesItem.itemNotFound')}</div>
          <button className="detail-breadcrumb-btn" onClick={() => navigate('/sales-items')}>
            {t('salesItem.backToList')}
          </button>
        </div>
      </Layout>
    );
  }

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  return (
    <Layout>
      <div className="detail-container">
        <nav className="detail-breadcrumb">
          <button
            className="detail-breadcrumb-btn"
            onClick={() => navigate('/sales-items')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('salesItem.backToList')}
          </button>
        </nav>

        <div className="detail-header">
          <div>
            <h1 className="detail-title">{item.productName}</h1>
            <div className="detail-meta">
              <span className="detail-meta-item">
                <code>{item.productSKU}</code>
              </span>
              <span className="detail-meta-item">
                <span className="badge badge-info">{item.salesChannel}</span>
              </span>
              <span className="detail-meta-item">
                <span className={`badge ${item.isActive ? 'badge-active' : 'badge-inactive'}`}>
                  {item.isActive ? t('salesItem.active') : t('salesItem.inactive')}
                </span>
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="detail-content">
          <div className="detail-main-section">

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('salesItem.productInformation')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('salesItem.productSKU')}</span>
                    <span className="detail-value"><code>{item.productSKU}</code></span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('salesItem.productName')}</span>
                    <span className="detail-value">{item.productName}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('salesItem.pricingDetails')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('salesItem.sellingPrice')}</span>
                    <span className="detail-value">
                      <strong>{Number(item.sellingPrice).toFixed(2)} {item.currency}</strong>
                    </span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('salesItem.currency')}</span>
                    <span className="detail-value">{item.currency}</span>
                  </div>
                  {item.minimumSellingPrice != null && (
                    <div className="detail-field">
                      <span className="detail-label">{t('salesItem.minimumSellingPrice')}</span>
                      <span className="detail-value">
                        {Number(item.minimumSellingPrice).toFixed(2)} {item.currency}
                      </span>
                    </div>
                  )}
                  {item.profitMarginPercentage != null && (
                    <div className="detail-field">
                      <span className="detail-label">{t('salesItem.profitMarginPercentage')}</span>
                      <span className="detail-value">{item.profitMarginPercentage.toFixed(2)}%</span>
                    </div>
                  )}
                  <div className="detail-field">
                    <span className="detail-label">{t('salesItem.salesChannel')}</span>
                    <span className="detail-value">
                      <span className="badge badge-info">{item.salesChannel}</span>
                    </span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('salesItem.tax')}</span>
                    <span className="detail-value">
                      {item.taxName
                        ? <span className="badge badge-info">{item.taxName} ({item.taxPercentage}%)</span>
                        : <span style={{ color: 'var(--ims-text-muted)' }}>—</span>}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('salesItem.policyDetails')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('salesItem.discountAllowed')}</span>
                    <span className="detail-value">
                      <span className={`badge ${item.discountAllowed ? 'badge-active' : 'badge-inactive'}`}>
                        {item.discountAllowed ? t('salesItem.yes') : t('salesItem.no')}
                      </span>
                    </span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('salesItem.returnAllowed')}</span>
                    <span className="detail-value">
                      <span className={`badge ${item.returnAllowed ? 'badge-active' : 'badge-inactive'}`}>
                        {item.returnAllowed ? t('salesItem.yes') : t('salesItem.no')}
                      </span>
                    </span>
                  </div>
                  {item.returnAllowed && item.returnPeriodDays != null && (
                    <div className="detail-field">
                      <span className="detail-label">{t('salesItem.returnPeriodDays')}</span>
                      <span className="detail-value">{item.returnPeriodDays} {t('salesItem.days')}</span>
                    </div>
                  )}
                  {item.warrantyPeriod && (
                    <div className="detail-field">
                      <span className="detail-label">{t('salesItem.warrantyPeriod')}</span>
                      <span className="detail-value">{item.warrantyPeriod}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('salesItem.auditInformation')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">Sales Item ID</span>
                    <span className="detail-value">{item.salesItemId}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('salesItem.createdDate')}</span>
                    <span className="detail-value">
                      {new Date(item.createdDate).toLocaleDateString(langCode)}{' '}
                      {new Date(item.createdDate).toLocaleTimeString(langCode)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="detail-sidebar">
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('common.actions')}</h3>
              </div>
              <div className="detail-card-body">
                <div className="detail-actions">
                  <button
                    className="btn-save"
                    onClick={() => navigate(`/sales-items/${item.salesItemId}/edit`)}
                  >
                    <PenLine size={16} strokeWidth={2} />
                    {t('salesItem.editItem')}
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => navigate(`/sales-items/${item.salesItemId}/delete`)}
                  >
                    <Trash2 size={16} strokeWidth={2} />
                    {t('common.delete')}
                  </button>
                  <button
                    className="detail-btn detail-btn-secondary"
                    onClick={() => navigate('/sales-items')}
                  >
                    <ArrowLeft size={16} strokeWidth={2} />
                    {t('salesItem.backToList')}
                  </button>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">Quick Actions</h3>
              </div>
              <div className="detail-card-body">
                <button
                  className="detail-quick-link"
                  onClick={() => navigate(`/products/${item.productId}`)}
                >
                  <LayoutGrid size={16} strokeWidth={2} />
                  View Product
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SalesItemDetailPage;
